import React from 'react';
import { Field, useFormState } from 'react-final-form';
import { FormattedMessage, useIntl } from 'react-intl';

import { RFFFieldErrorWrapper } from 'common/components';
import { useLocaleUtils } from 'common/hooks';
import { ItemQuantity } from 'modules/menu/items/types';
import { useOfferingMenuPermissionsContext } from 'modules/shared-menu/contexts/OfferingMenuPermissionsContext';

import { InventoryItemQuantity, InventoryItemQuantityView } from './InventoryStock';
import { BaseField } from './types';
import { useOfferOverridesContext } from '../../../context/OfferOverridesContext';
import { FormRowRenderer } from '../FormRowRenderer';
import { PreviewRowContainer, InventoryData, StyledInputNumber } from '../OfferDetailsFormFields.styles';
import { InventoryManagementFormValues, FormValues } from '../types';

type Props = BaseField & {
  offerQuantity: ItemQuantity | undefined;
};

export const CurrentAmount = ({ sidePanelMode, offerQuantity }: Props) => {
  const menuPermissions = useOfferingMenuPermissionsContext();
  const intl = useIntl();
  const { values } = useFormState<FormValues>();
  const { getLocalizedUnit } = useLocaleUtils();
  const isWeightBasedItem = values.weightType === 'weighted-items';
  const availableItems = offerQuantity?.available;
  const reservedItems = offerQuantity?.reserved;
  const { maybeOverriddenOffer } = useOfferOverridesContext();

  const previewComponent = (
    <PreviewRowContainer>
      <div>
        <InventoryItemQuantityView
          quantity={offerQuantity?.balance}
          inputType={maybeOverriddenOffer?.sell_by_weight_config?.input_type}
        />
        <InventoryData>
          <FormattedMessage id="inventory.reserved-stock" />:{' '}
          <InventoryItemQuantityView
            quantity={reservedItems}
            inputType={maybeOverriddenOffer?.sell_by_weight_config?.input_type}
          />
        </InventoryData>
        <InventoryData>
          <FormattedMessage id="inventory.available-stock" />:{' '}
          <InventoryItemQuantityView
            quantity={availableItems}
            inputType={maybeOverriddenOffer?.sell_by_weight_config?.input_type}
          />
        </InventoryData>
      </div>
    </PreviewRowContainer>
  );

  const editComponent = (
    <>
      <Field<InventoryManagementFormValues['inventoryBalance']> name="inventoryBalance">
        {field => (
          <RFFFieldErrorWrapper field={field}>
            {input => (
              <StyledInputNumber
                {...input}
                suffixText={
                  values.sellByWeightInputType === 'grams' && isWeightBasedItem ? getLocalizedUnit('gram') : undefined
                }
              />
            )}
          </RFFFieldErrorWrapper>
        )}
      </Field>
      {reservedItems !== undefined && (
        <InventoryData>
          <FormattedMessage id="inventory.reserved" />:{' '}
          <InventoryItemQuantity
            quantity={reservedItems}
            sellByWeightInputType={values.sellByWeightInputType}
            isWeightBasedItem={isWeightBasedItem}
          />
        </InventoryData>
      )}
      {availableItems !== undefined && (
        <InventoryData>
          <FormattedMessage id="inventory.available" />:{' '}
          <InventoryItemQuantity
            quantity={availableItems}
            sellByWeightInputType={values.sellByWeightInputType}
            isWeightBasedItem={isWeightBasedItem}
          />
        </InventoryData>
      )}
    </>
  );

  return (
    <FormRowRenderer
      hasContentEditPermission={menuPermissions.contentEdit}
      label={intl.formatMessage({ id: 'inventory.current' })}
      previewComponent={previewComponent}
      editComponent={editComponent}
      sidePanelMode={sidePanelMode}
      offer={maybeOverriddenOffer}
      required
      isOnlyForVenueLevel
    />
  );
};
